// Machine identification card shown at the top of MachineQRCodeDialog — the
// "you are printing a sticker for THIS machine" confirmation (photo, display
// name, internal number, serial).
//
// Extracted verbatim from MachineQRCodeDialog during D7-FE1 purely to keep both
// files under the 300-line rule; the rendered markup is unchanged.
import React from "react";
import { Box } from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { Machine } from "../models/models";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import { dt } from "../utils/dt";

export interface MachineQRCodeDialogIdentityProps {
  machine: Machine;
}

const MachineQRCodeDialogIdentity = ({ machine }: MachineQRCodeDialogIdentityProps) => {
  const displayName = getMachineDisplayName(machine);
  const machineImage = machine.image?.content || machine.catalogModel?.imageStandardUrl || "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        backgroundColor: dt.inputBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: dt.radiusSm,
          border: `1px solid ${dt.cardBorder}`,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#F8FAFC",
          flexShrink: 0,
        }}
      >
        {machineImage ? (
          <img
            src={machineImage}
            alt={displayName}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <QrCode2Icon sx={{ fontSize: 20, color: dt.textMuted }} />
        )}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 600,
            color: dt.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 1,
            fontFamily: dt.fontMono,
            fontSize: "12px",
            fontWeight: 500,
            color: dt.textSecondary,
            letterSpacing: "0.3px",
          }}
        >
          <Box component="span">{machine.internalNumber}</Box>
          {machine.serialNumber && (
            <>
              <Box component="span" sx={{ color: dt.textMuted }}>
                ·
              </Box>
              <Box component="span">S/N: {machine.serialNumber}</Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MachineQRCodeDialogIdentity;
